<template>
  <div class="tw-flex tw-items-center tw-w-full">
    <div class="tw-flex-grow tw-flex tw-justify-center">
      <template v-if="isPending">
        <WdsTooltip
          v-if="awaitingClientApproval"
          :global="tableGlobals"
        >
          <template #tooltip-content>
            {{ $t('Awaiting end client approval') }}
          </template>
          <WdsButtonGhost
            secondary
            size="sm"
            disabled
            data-test="client-payment-request-review-button"
          >
            {{ $t('Review') }}
          </WdsButtonGhost>
        </WdsTooltip>
        <WdsButtonGhost
          v-else
          secondary
          size="sm"
          data-test="client-payment-request-review-button"
          @click="openReviewModal"
        >
          {{ $t('Review') }}
        </WdsButtonGhost>
      </template>
      <WdsButtonGhost
        v-else
        size="sm"
        data-test="client-payment-request-view-button"
        @click="openReviewModal"
      >
        {{ $t('View') }}
      </WdsButtonGhost>
    </div>

    <div v-if="!simpleMode" class="tw-flex-shrink-0 tw-ml-2">
      <WdsMenu
        z-index="50"
        placement="bottom-end"
        manual-trigger
        :global="tableGlobals"
      >
        <template #trigger="{ toggle }">
          <WdsButtonGhost
            :data-test="`client-payment-request-row-actions-${input.id}`"
            size="sm"
            @click="toggle"
          >
            <WdsIcon name="overflow-menu-horizontal" :size="16" />
          </WdsButtonGhost>
        </template>

        <div>
          <WdsMenuOption v-if="!awaitingClientApproval" @click="openReviewModal">
            <WdsIcon name="receipt" :size="16" />
            {{ isPending ? $t('Review') : $t('View request') }}
          </WdsMenuOption>

          <WdsMenuOption v-if="input.hire" :href="hirePath">
            <WdsIcon name="hire" :size="16" />
            {{ $t('View hire details') }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="input.hire?.conversation?.id"
            :href="messagePath"
          >
            <WdsIcon name="chat" :size="16" />
            {{
              input.hire?.recruiter && input.hire?.recruiterManagesWorkers
                ? $t('Message staffing agency')
                : $t('Message worker')
            }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="input.hire"
            :href="contractPdfPath"
            target="_blank"
          >
            <WdsIcon name="download" :size="16" />
            {{ $t('Download contract (PDF)') }}
          </WdsMenuOption>

          <WdsMenuOption
            :href="requestPdfPath"
            target="_blank"
          >
            <WdsIcon name="download" :size="16" />
            {{ $t('Download request (PDF)') }}
          </WdsMenuOption>

          <WdsMenuOption
            v-if="input.invoice?.pdfUrl"
            :href="input.invoice.pdfUrl"
            target="_blank"
          >
            <WdsIcon name="download" :size="16" />
            {{ $t('Download invoice (PDF)') }}
          </WdsMenuOption>
        </div>
      </WdsMenu>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { WdsButtonGhost, WdsIcon, WdsMenu, WdsMenuOption, WdsTooltip } from '@worksome/wds-components'
import { computed } from 'vue'
import { FragmentType, graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import namedRoutes from '@/utils/laravelNamedRoutes.json'
import { useTableGlobalsSelector } from '@/components/GraphqlDataTable/injection'

const tableGlobals = useTableGlobalsSelector()

const PaymentRequestActionColumn = graphql(`
  fragment PaymentRequestActionColumn on PaymentRequest {
    id
    number
    cancelledAt
    rejectedAt
    paidAt
    manuallyApprovedAt
    autoApprovedAt
    clientPaymentRequest {
      id
    }
    hire {
      id
      conversation {
        id
      }
      recruiter {
        id
      }
      recruiterManagesWorkers
    }
    invoice {
      id
      pdfUrl
    }
  }
`)

const props = withDefaults(
  defineProps<{
    row: FragmentType<typeof PaymentRequestActionColumn>
    simpleMode?: boolean
  }>(),
  {
    simpleMode: false,
  },
)

const input = computed(() => useFragment(PaymentRequestActionColumn, props.row))

const billId = computed(() => {
  const globalId = input.value.id

  try {
    const decoded = atob(globalId)
    return decoded.split(':').pop() ?? ''
  } catch {
    return ''
  }
})

const redirectUri = computed(() => {
  if (typeof window === 'undefined') {
    return ''
  }

  return encodeURIComponent(`${window.location.pathname}${window.location.search}`)
})

const isPending = computed(
  () =>
    !input.value.cancelledAt &&
    !input.value.rejectedAt &&
    !input.value.paidAt &&
    !input.value.manuallyApprovedAt &&
    !input.value.autoApprovedAt,
)

// When a client-side mirror exists, the end client owns the approval
// decision and it propagates back to this PR. Review action on this side
// would be misleading.
const awaitingClientApproval = computed(
  () => isPending.value && Boolean(input.value.clientPaymentRequest),
)

const openReviewModal = () => {
  if (!billId.value) {
    return
  }

  if (typeof window === 'undefined') {
    return
  }

  const paymentModalSelector = '#paymentmodal'
  const paymentModalContentSelector = '#paymentmodal-content'
  const formUrl =
    redirectUri.value === ''
      ? `/payments/create?bill_id=${billId.value}`
      : `/payments/create?bill_id=${billId.value}&redirect_uri=${redirectUri.value}`

  const jQueryInstance = (window as Window & { $?: any }).$

  if (jQueryInstance) {
    const modalContent = jQueryInstance(paymentModalContentSelector)
    const modal = jQueryInstance(paymentModalSelector)

    if (modalContent.length && modal.length) {
      modalContent.html('')
      modalContent.load(formUrl, (_responseText: unknown, textStatus: string) => {
        if (textStatus === 'error') {
          modalContent.html(`<div class="modal-content">${$t('Error in loading bill')}</div>`)
        }
      })
      modal.modal({ backdrop: 'static', keyboard: false }, 'show')

      return
    }
  }

  window.location.href = formUrl
}

const hirePath = computed(() =>
  input.value.hire?.id
    ? laravelNamedRoute(namedRoutes.hires.hire.view, {
        hire: input.value.hire.id,
        tab: 'overview',
      })
    : '#',
)

const messagePath = computed(() =>
  input.value.hire?.conversation?.id
    ? laravelNamedRoute(namedRoutes.messages.show, {
        id: input.value.hire.conversation.id,
      })
    : '#',
)

const contractPdfPath = computed(() =>
  input.value.hire?.id
    ? laravelNamedRoute(namedRoutes.yjob.contract, {
        hire: input.value.hire.id,
      })
    : '#',
)

const requestPdfPath = computed(() =>
  billId.value
    ? laravelNamedRoute(namedRoutes.bills.pdf, {
        bill: billId.value,
      })
    : '#',
)
</script>
